import { useCallback, useState } from "react";

export interface CommittedFiltersResult<T extends object> {
  /** 控件正在编辑的筛选条件 */
  draftFilters: T;
  /** 已提交并用于查询、分页和导出的筛选条件 */
  appliedFilters: T;
  /** 只更新编辑态 */
  updateDraftFilters: (patch: Partial<T>) => void;
  /** 提交编辑态，并同步返回本次提交的条件 */
  commitFilters: () => T;
  /** 同时替换编辑态和已提交态 */
  replaceFilters: (filters: T) => T;
}

const cloneFilters = <T extends object>(filters: T): T => ({ ...filters });

/**
 * 管理筛选条件的编辑态和已提交态，避免输入过程直接影响当前查询。
 */
const useCommittedFilters = <T extends object>(
  initialFilters: T,
): CommittedFiltersResult<T> => {
  const [draftFilters, setDraftFilters] = useState<T>(() =>
    cloneFilters(initialFilters),
  );
  const [appliedFilters, setAppliedFilters] = useState<T>(() =>
    cloneFilters(initialFilters),
  );

  const updateDraftFilters = useCallback((patch: Partial<T>) => {
    setDraftFilters(current => ({ ...current, ...patch }));
  }, []);

  const commitFilters = useCallback((): T => {
    // 同步返回新条件，调用方无需等待下一次渲染即可请求第一页。
    const nextFilters = cloneFilters(draftFilters);
    setAppliedFilters(nextFilters);
    return nextFilters;
  }, [draftFilters]);

  const replaceFilters = useCallback((filters: T): T => {
    const nextFilters = cloneFilters(filters);
    setDraftFilters(nextFilters);
    setAppliedFilters(nextFilters);
    return nextFilters;
  }, []);

  return {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  };
};

export default useCommittedFilters;
